/**
 * Variant / inventory helpers for wholesale product pages.
 */

export function normalizeAttributeValue(value) {
  if (value == null) return '';
  return String(value).trim().replace(/\s+/g, ' ');
}

/** Normalize attribute map keys and values for reliable matching. */
export function normalizeAttributes(attributes = {}) {
  return Object.fromEntries(
    Object.entries(attributes || {}).map(([key, value]) => [
      normalizeAttributeValue(key),
      normalizeAttributeValue(value),
    ])
  );
}

/** True when inventory satisfies every non-empty selected attribute. */
export function inventoryMatchesSelection(inventory, selectedPartial = {}) {
  const invAttrs = normalizeAttributes(inventory?.attributes);
  return Object.entries(selectedPartial)
    .filter(([, value]) => normalizeAttributeValue(value))
    .every(
      ([key, value]) =>
        invAttrs[normalizeAttributeValue(key)] === normalizeAttributeValue(value)
    );
}

export function findMatchingInventories(inventories = [], selectedPartial = {}) {
  return (inventories || []).filter((inv) =>
    inventoryMatchesSelection(inv, selectedPartial)
  );
}

/** Unique option values per attribute from live inventory rows (source of truth). */
export function getUniqueAttributeOptions(inventories = []) {
  const buckets = {};

  (inventories || []).forEach((inventory) => {
    const attrs = normalizeAttributes(inventory.attributes);
    Object.entries(attrs).forEach(([key, value]) => {
      if (!value) return;
      if (!buckets[key]) buckets[key] = new Map();

      const existing = buckets[key].get(value);
      if (!existing) {
        buckets[key].set(value, {
          value,
          thumbnail: inventory.thumbnail_image || null,
          sampleInventory: inventory,
        });
      } else if (!existing.thumbnail && inventory.thumbnail_image) {
        existing.thumbnail = inventory.thumbnail_image;
      }
    });
  });

  return Object.fromEntries(
    Object.entries(buckets).map(([key, map]) => [key, Array.from(map.values())])
  );
}

/** Preferred attribute order for wholesale (size before color). */
export function getAttributeKeys(inventories = [], productAttributes = {}) {
  const fromInventories = new Set();
  (inventories || []).forEach((inv) => {
    Object.keys(normalizeAttributes(inv.attributes)).forEach((k) =>
      fromInventories.add(k)
    );
  });

  const preferred = ['Size', 'Color'];
  const ordered = preferred.filter((k) => fromInventories.has(k));
  fromInventories.forEach((k) => {
    if (!ordered.includes(k)) ordered.push(k);
  });

  if (ordered.length === 0 && productAttributes) {
    return Object.keys(productAttributes).filter(
      (k) => productAttributes[k]?.length > 0
    );
  }

  return ordered;
}

/** Normalize image URLs for reliable cross-field matching. */
export function normalizeImageUrl(url) {
  if (!url) return '';
  return String(url).trim().split('?')[0].replace(/\/$/, '');
}

export function inventoryHasImage(inventory, url) {
  const target = normalizeImageUrl(url);
  if (!target) return false;
  return getInventoryGalleryImages(inventory).some(
    (img) => normalizeImageUrl(img) === target
  );
}

export function getInventoryGalleryImages(inventory) {
  if (!inventory) return [];
  return [
    inventory.thumbnail_image,
    ...(Array.isArray(inventory.extra_images)
      ? inventory.extra_images
          .filter((img) => img.is_active)
          .map((img) => img.image)
      : []),
  ].filter(Boolean);
}

/** All unique product + variant images for the gallery carousel. */
export function getAllProductGalleryImages(product, inventories = []) {
  const seen = new Set();
  const urls = [];

  const add = (url) => {
    const norm = normalizeImageUrl(url);
    if (norm && !seen.has(norm)) {
      seen.add(norm);
      urls.push(url);
    }
  };

  add(product?.thumbnail_image);
  (inventories || []).forEach((inv) => {
    getInventoryGalleryImages(inv).forEach(add);
  });

  return urls;
}

/** Inventories whose gallery includes this URL (incl. product hero fallback). */
export function getInventoriesForGalleryImage(
  allImages,
  inventories,
  index,
  product
) {
  const url = allImages?.[index];
  if (!url) return [];

  let matches = (inventories || []).filter((inv) => inventoryHasImage(inv, url));

  const normUrl = normalizeImageUrl(url);
  const normProductThumb = normalizeImageUrl(product?.thumbnail_image);

  if (matches.length === 0 && normProductThumb && normProductThumb === normUrl) {
    matches = (inventories || []).filter(
      (inv) => normalizeImageUrl(inv.thumbnail_image) === normUrl
    );
  }

  if (matches.length === 0 && normProductThumb === normUrl && inventories?.length) {
    matches = [
      inventories.find((inv) => inv.stock > 0) ||
        inventories[0],
    ];
  }

  return matches.filter(Boolean);
}

/** Pick the inventory row that best owns a gallery image. */
export function pickInventoryForGalleryImage(matches, url) {
  if (!matches?.length) return null;
  const normUrl = normalizeImageUrl(url);

  const thumbnailOwner = matches.find(
    (inv) => normalizeImageUrl(inv.thumbnail_image) === normUrl
  );
  if (thumbnailOwner) return thumbnailOwner;

  if (matches.length === 1) return matches[0];

  const inStock = matches.find((inv) => inv.stock > 0);
  return inStock || matches[0];
}

/** Resolve variant attributes for a carousel index. */
export function getSelectionForGalleryIndex(
  allImages,
  inventories,
  index,
  product
) {
  const url = allImages?.[index];
  const matches = getInventoriesForGalleryImage(
    allImages,
    inventories,
    index,
    product
  );
  const pick = pickInventoryForGalleryImage(matches, url);
  return pick ? normalizeAttributes(pick.attributes) : null;
}

/** Pre-built map: every carousel index → variant selection (for instant sync). */
export function buildGalleryImageInventoryMap(product, inventories) {
  const allImages = getAllProductGalleryImages(product, inventories);
  return allImages.map((url, index) => {
    const matches = getInventoriesForGalleryImage(
      allImages,
      inventories,
      index,
      product
    );
    const inventory = pickInventoryForGalleryImage(matches, url);
    const selection = inventory ? normalizeAttributes(inventory.attributes) : null;
    return {
      index,
      url,
      inventory,
      selection,
      label: selection
        ? Object.values(selection).filter(Boolean).join(' · ')
        : null,
    };
  });
}

/** Human label for the variant tied to a gallery image. */
export function getVariantLabelForGalleryIndex(
  allImages,
  inventories,
  index,
  product
) {
  const selection = getSelectionForGalleryIndex(
    allImages,
    inventories,
    index,
    product
  );
  if (!selection) return null;
  return Object.values(selection).filter(Boolean).join(' · ') || null;
}

/** Per-slide metadata for the full gallery (all images browsable). */
export function buildGallerySlideMeta(allImages, inventories, product) {
  return (allImages || []).map((url, index) => {
    const matches = getInventoriesForGalleryImage(allImages, inventories, index, product);
    const inventory = pickInventoryForGalleryImage(matches, url);
    const selection = inventory ? normalizeAttributes(inventory.attributes) : null;
    return {
      index,
      url,
      ownerCount: matches.length,
      selection,
      label: selection
        ? Object.values(selection).filter(Boolean).join(' · ')
        : null,
    };
  });
}

/** Index of the first image for the current variant selection (for carousel focus). */
export function getGalleryFocusIndex(allImages, inventories = [], selectedPartial = {}) {
  return getGalleryIndicesForSelection(allImages, inventories, selectedPartial).focusIndex;
}

/** Focus index + all carousel indices belonging to the selected variant(s). */
export function getGalleryIndicesForSelection(
  allImages,
  inventories = [],
  selectedPartial = {}
) {
  const matching = findMatchingInventories(inventories, selectedPartial);
  if (!matching.length || !allImages?.length) {
    return { focusIndex: 0, variantIndices: [] };
  }

  const variantIndices = new Set();
  matching.forEach((inv) => {
    getInventoryGalleryImages(inv).forEach((url) => {
      const idx = allImages.findIndex(
        (u) => normalizeImageUrl(u) === normalizeImageUrl(url)
      );
      if (idx >= 0) variantIndices.add(idx);
    });
  });

  const sorted = [...variantIndices].sort((a, b) => a - b);
  return {
    focusIndex: sorted[0] ?? 0,
    variantIndices: sorted,
  };
}

/** Stable key — changes when any attribute selection changes. */
export function getSelectionKey(selectedPartial = {}) {
  return Object.entries(selectedPartial)
    .filter(([, value]) => normalizeAttributeValue(value))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${normalizeAttributeValue(value)}`)
    .join('|');
}

/** @deprecated use getAllProductGalleryImages */
export function getGalleryImagesForSelection(
  product,
  inventories = [],
  selectedPartial = {},
  attributeKeys = []
) {
  return getAllProductGalleryImages(product, inventories);
}

/** Pick first in-stock inventory for default selection. */
export function getInitialSelection(inventories = []) {
  const list = inventories || [];
  const preferred =
    list.find((inv) => inv.stock > 0) || list[0];
  return preferred ? normalizeAttributes(preferred.attributes) : {};
}

/** Auto-fill attributes that only have one valid choice given current picks. */
export function autoCompleteSelection(inventories = [], selected = {}, attributeKeys = []) {
  const next = { ...selected };

  attributeKeys.forEach((key) => {
    if (normalizeAttributeValue(next[key])) return;

    const matches = findMatchingInventories(inventories, next);
    const values = new Set();
    matches.forEach((inv) => {
      const v = normalizeAttributes(inv.attributes)[key];
      if (v) values.add(v);
    });

    if (values.size === 1) {
      next[key] = [...values][0];
    }
  });

  return next;
}

export function isSelectionComplete(selected = {}, attributeKeys = []) {
  return attributeKeys.every((key) => normalizeAttributeValue(selected[key]));
}

export function getOptionMeta(inventories = [], attributeKey, optionValue, selectedPartial = {}) {
  const matches = findMatchingInventories(inventories, {
    ...selectedPartial,
    [attributeKey]: optionValue,
  });
  const inStock = matches.filter((inv) => inv.stock > 0);
  const stock = matches.reduce((sum, inv) => sum + (inv.stock || 0), 0);
  const prices = matches.map((inv) => parseFloat(inv.price)).filter(Number.isFinite);

  return {
    matches,
    inStockCount: inStock.length,
    totalStock: stock,
    isAvailable: inStock.length > 0,
    thumbnail: matches[0]?.thumbnail_image || null,
    priceMin: prices.length ? Math.min(...prices) : null,
    priceMax: prices.length ? Math.max(...prices) : null,
  };
}

export function formatVariantSummary(inventory, product) {
  if (!inventory) return null;
  const attrs = normalizeAttributes(inventory.attributes);
  const parts = Object.values(attrs).filter(Boolean);
  const moq =
    parseInt(inventory?.properties?.product_info?.minimum_order_quantity, 10) ||
    parseInt(product?.minimum_order_quantity, 10) ||
    1;

  return {
    label: parts.join(' · ') || 'Selected variant',
    sku: inventory.sku,
    price: inventory.price,
    stock: inventory.stock,
    moq: moq > 0 ? moq : 1,
  };
}
